import { ENERGY, TASK, TIME, type TaskId } from '../config/constants';
import type { Game } from '../core/Game';
import { formatClock } from '../core/GameState';
import type { UI } from './UI';
import { icon, type IconName } from '../art/icons';

const TUTORIAL_STEPS: TaskId[] = ['feed', 'milk', 'pasteurize', 'curdle', 'rennet', 'cut', 'stretch', 'bag', 'sell'];

export class Hud {
  private hud = document.getElementById('hud') as HTMLElement;
  private bottom = document.getElementById('bottombar') as HTMLElement;
  private checklist = document.getElementById('checklist') as HTMLElement;
  private clockEl!: HTMLElement;
  private phaseEl!: HTMLElement;
  private energyFill!: HTMLElement;
  private energyText!: HTMLElement;
  private goldEl!: HTMLElement;
  private gemsEl!: HTMLElement;
  private levelEl!: HTMLElement;
  private focusChip!: HTMLElement;
  private endDayBtn!: HTMLButtonElement;
  private staffBtn!: HTMLButtonElement;
  private devTimeBtn!: HTMLButtonElement;
  private devChip!: HTMLElement;
  private devModeBtn!: HTMLButtonElement;

  constructor(
    private game: Game,
    private ui: UI,
  ) {
    this.build();
  }

  private build(): void {
    this.hud.innerHTML = `
      <div class="chip">${icon('day', 'Day')} <b class="day"></b> · <b class="clock"></b> <span class="muted phase"></span></div>
      <div class="chip energy">${icon('energy', 'Energy')} <span class="energy-bar"><span class="energy-fill" style="display:block"></span></span><b class="energy-text"></b></div>
      <div class="chip">${icon('gold', 'Coins')} <b class="gold"></b></div>
      <div class="chip">${icon('gems', 'Gems')} <b class="gems gem"></b></div>
      <div class="chip">${icon('level', 'Level')} <b class="level"></b></div><span class="version-badge">1G</span>
      <span class="spacer"></span>
      <div class="chip focus-chip" style="display:none"></div>
      <div class="chip dev-chip gem" style="display:none" title="Dev mode: everything unlocked, energy pinned">DEV</div>`;
    this.clockEl = this.hud.querySelector('.clock') as HTMLElement;
    this.phaseEl = this.hud.querySelector('.phase') as HTMLElement;
    this.energyFill = this.hud.querySelector('.energy-fill') as HTMLElement;
    this.energyText = this.hud.querySelector('.energy-text') as HTMLElement;
    this.goldEl = this.hud.querySelector('.gold') as HTMLElement;
    this.gemsEl = this.hud.querySelector('.gems') as HTMLElement;
    this.levelEl = this.hud.querySelector('.level') as HTMLElement;
    this.focusChip = this.hud.querySelector('.focus-chip') as HTMLElement;
    this.devChip = this.hud.querySelector('.dev-chip') as HTMLElement;

    this.bottom.innerHTML = '';
    const mk = (label: string, key: string, onClick: () => void, cls = 'btn', iconName?: IconName): HTMLButtonElement => {
      const b = document.createElement('button');
      b.className = cls;
      b.innerHTML = `${iconName ? icon(iconName) : ''}${label}${key ? ` <span class="kbd">${key}</span>` : ''}`;
      b.onclick = onClick;
      this.bottom.appendChild(b);
      return b;
    };
    mk('Inventory', 'I', () => this.ui.openInventory(), 'btn', 'inventory');
    mk('Codex', 'C', () => this.ui.openCodex(), 'btn', 'codex');
    mk('Mastery', 'M', () => this.ui.openMastery(), 'btn', 'mastery');
    mk('Growing Farm', 'K', () => this.ui.openSkills(), 'btn', 'skills');
    mk('Research', '', () => (this.ui as UI & { openResearch?: () => void }).openResearch?.(), 'btn', 'skills');
    mk('Supplies', 'B', () => this.ui.openSupplies(), 'btn', 'supplies');
    this.staffBtn = mk('Staff', 'T', () => this.ui.openStaff(), 'btn', 'staff');
    this.endDayBtn = mk('End Day', 'N', () => this.ui.openEndDay());
    const spacer = document.createElement('span');
    spacer.className = 'spacer';
    this.bottom.appendChild(spacer);
    mk('Save', '', () => {
      this.game.save();
      this.game.toast('Saved.', 'info');
    }, 'btn', 'save');

    const dev = document.createElement('div');
    dev.className = 'dev';
    dev.innerHTML = `<span class="muted">dev</span>`;
    const devBtn = (label: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.className = 'btn small';
      b.textContent = label;
      b.onclick = fn;
      dev.appendChild(b);
      return b;
    };
    this.devTimeBtn = devBtn('time ×1', () => {
      this.game.devTimeScale = this.game.devTimeScale === 1 ? 10 : 1;
      this.devTimeBtn.textContent = `time ×${this.game.devTimeScale}`;
    });
    devBtn('+100 gold', () => {
      this.game.state.gold += 100;
      this.game.emit('change', undefined);
    });
    devBtn('energy', () => {
      this.game.state.energy = ENERGY.MAX;
      this.game.emit('change', undefined);
    });
    devBtn('→14:00', () => {
      const target = this.game.workDayEnd;
      if (this.game.state.minuteOfDay < target) this.game.advanceMinutes(target - this.game.state.minuteOfDay);
    });
    this.devModeBtn = devBtn('unlock all', () => {
      if (this.game.devMode) this.game.disableDevMode();
      else this.game.enableDevMode();
    });
    this.bottom.appendChild(dev);
  }

  update(): void {
    const s = this.game.state;
    (this.hud.querySelector('.day') as HTMLElement).textContent = String(s.day);
    this.clockEl.textContent = formatClock(s.minuteOfDay);
    const phase = s.minuteOfDay >= this.game.workDayEnd ? 'Overtime' : 'Work day';
    this.phaseEl.textContent = phase;
    this.phaseEl.className = `muted phase ${phase === 'Overtime' ? 'bad' : ''}`;
    const e = Math.round(s.energy);
    this.energyFill.style.width = `${(s.energy / ENERGY.MAX) * 100}%`;
    this.energyFill.classList.toggle('low', s.energy < ENERGY.LOW_THRESHOLD);
    this.energyText.textContent = String(e);
    (this.hud.querySelector('.energy') as HTMLElement).classList.toggle('warn', s.energy < ENERGY.LOW_THRESHOLD);
    this.goldEl.textContent = s.gold.toFixed(2);
    this.gemsEl.textContent = String(s.gems);
    this.levelEl.textContent = String(this.game.overallLevel);
    if (this.game.paused) {
      this.focusChip.style.display = '';
      this.focusChip.textContent = '⏸ Paused for reading';
    } else if (this.game.focus) {
      this.focusChip.style.display = '';
      this.focusChip.textContent = `⏩ Focus ×${TIME.FOCUS_TIME_SCALE}`;
    } else this.focusChip.style.display = 'none';
    this.endDayBtn.disabled = !this.game.canEndDay;
    this.endDayBtn.classList.toggle('primary', this.game.canEndDay);
    this.staffBtn.classList.toggle('dim', !this.game.hasStaff);
    this.devChip.style.display = this.game.devMode ? '' : 'none';
    this.devModeBtn.textContent = this.game.devMode ? 'dev mode: on' : 'unlock all';
    this.devModeBtn.classList.toggle('active', this.game.devMode);
    this.renderChecklist();
  }

  private renderChecklist(): void {
    const t = this.game.state.tutorial;
    if (t.done) {
      this.checklist.innerHTML = '';
      return;
    }
    const next = TUTORIAL_STEPS.find((step) => !t.completed.includes(step));
    const html = TUTORIAL_STEPS.map((step) => {
      const cls = t.completed.includes(step) ? 'done' : step === next ? 'next' : '';
      return `<span class="${cls}">${TASK[step].label}</span>`;
    }).join('<span class="muted">›</span>');
    const markup = `<div class="checklist-row">${html}</div>`;
    if (this.checklist.innerHTML !== markup) this.checklist.innerHTML = markup;
  }
}
